import React from 'react';
import classNames from 'classnames';
import { BASE_IMG_URL } from '@images/imageUrls';
import styles from './index.module.scss';
import type { FeedbackProblemCategory, FeedbackProblemItem, FeedbackProblemPickerProps } from '../index';
import { findProblemByValue } from '../index';
import { releaseModalBodyScrollLock, syncModalBodyScrollLock } from '@utils/modalScrollLock';

type TransitionState = 'hidden' | 'entering' | 'entered' | 'exiting';

interface PCFeedbackProblemPickerState {
    selectedValue: string;
    selectedProblem: FeedbackProblemItem | null;
    transitionState: TransitionState;
}

class PCFeedbackProblemPicker extends React.Component<FeedbackProblemPickerProps, PCFeedbackProblemPickerState> {
    state: PCFeedbackProblemPickerState = {
        selectedValue: '',
        selectedProblem: null,
        transitionState: 'hidden',
    };

    transitionTimer: ReturnType<typeof setTimeout> | null = null;
    animationFrames: number[] = [];

    componentDidMount() {
        if (this.props.visible) {
            syncModalBodyScrollLock(true, false);
            this.openPicker();
        }
    }

    openPicker = () => {
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
            this.transitionTimer = null;
        }
        const selectedValue = String(this.props.value || '');
        this.setState({
            selectedValue,
            selectedProblem: findProblemByValue(this.props.categories, selectedValue),
            transitionState: 'entering',
        });
        this.animationFrames.push(requestAnimationFrame(() => {
            this.animationFrames.push(requestAnimationFrame(() => {
                this.setState({ transitionState: 'entered' });
            }));
        }));
        document.addEventListener('keydown', this.handleKeyDown);
    };

    /** 弹框显隐变化时：锁定/解锁页面滚动，并重置选中值 */
    componentDidUpdate(prevProps: FeedbackProblemPickerProps) {
        // 弹框打开时禁止页面滚动，仅允许弹框内列表滚动
        syncModalBodyScrollLock(this.props.visible, prevProps.visible);

        if (this.props.visible && !prevProps.visible) {
            this.openPicker();
        }

        if (!this.props.visible && prevProps.visible) {
            this.animationFrames.forEach(cancelAnimationFrame);
            this.animationFrames = [];
            this.setState({ transitionState: 'exiting' });
            this.transitionTimer = setTimeout(() => {
                this.setState({ transitionState: 'hidden' });
                this.transitionTimer = null;
            }, 200);
            document.removeEventListener('keydown', this.handleKeyDown);
        }
    }

    /** 组件卸载时释放滚动锁 */
    componentWillUnmount() {
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
        }
        this.animationFrames.forEach(cancelAnimationFrame);
        document.removeEventListener('keydown', this.handleKeyDown);
        releaseModalBodyScrollLock(this.props.visible);
    }

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !e.defaultPrevented) {
            this.props.onClose();
        }
    };

    /** 确定：回传当前选中的问题类型 */
    handleConfirm = () => {
        const { onConfirm } = this.props;
        const { selectedProblem } = this.state;
        if (this.props.requireSelection && !selectedProblem) return;
        onConfirm(selectedProblem);
    };

    /** 重置：清空当前选中项，不关闭弹框 */
    handleReset = () => {
        this.setState({ selectedValue: '', selectedProblem: null });
    };

    /** 渲染居中弹窗选择器 */
    render() {
        const { categories, onClose, secondaryAction = 'reset', requireSelection = false } = this.props;
        const { selectedValue, transitionState } = this.state;
        const isVisible = transitionState === 'entered';

        if (transitionState === 'hidden') return null;

        return (
            <div
                className={classNames(styles.mask, isVisible && styles.maskVisible)}
                onClick={onClose}
            >
                <div
                    className={classNames(styles.modal, this.props.variant === 'conversion' && styles.conversion, isVisible && styles.modalVisible)}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.header}>
                        <div className={styles.title}>问题类型</div>
                        <button
                            type="button"
                            aria-label="关闭问题类型"
                            className={styles.closeBtn}
                            onClick={onClose}
                        >
                            <img src={`${BASE_IMG_URL}/feedback/icon4.png`} alt="" />
                        </button>
                    </div>

                    <div className={styles.body} data-body-scroll-lock-scrollable>
                        {categories.map((category: FeedbackProblemCategory) => (
                            <div key={category.id} className={styles.section}>
                                {category.name ? (
                                    <div className={styles.sectionTitle}>{category.name}</div>
                                ) : null}
                                <div className={styles.tagList}>
                                    {category.problems.map((problem) => {
                                        const checked = selectedValue === problem.value;
                                        return (
                                            <button
                                                type="button"
                                                aria-pressed={checked}
                                                key={problem.value}
                                                className={classNames(styles.tag, checked && styles.tagActive)}
                                                onClick={() => this.setState({
                                                    selectedValue: problem.value,
                                                    selectedProblem: problem,
                                                })}
                                            >
                                                {problem.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                        {!categories.length && (
                            <div className={styles.emptyText}>暂无问题类型</div>
                        )}
                    </div>

                    <div className={styles.footer}>
                        <button type="button" className={styles.cancelBtn} onClick={secondaryAction === 'cancel' ? onClose : this.handleReset}>{secondaryAction === 'cancel' ? '取消' : '重置'}</button>
                        <button type="button" className={styles.confirmBtn} disabled={requireSelection && !this.state.selectedProblem} onClick={this.handleConfirm}>确定</button>
                    </div>
                </div>
            </div>
        );
    }
}

export default PCFeedbackProblemPicker;
